import { useEffect, useState } from "react";
import { KeyRound, QrCode, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function CheckinCard({ onVerified }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const verify = async (method) => {
    if (!eventId) { toast.error("Pilih kegiatan terlebih dahulu."); return; }
    if (method === "pin" && code.trim().length < 3) {
      toast.error("Masukkan kode PIN dari panitia (contoh: BN-429).");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkin", {
        event_id: eventId, method, code,
        user_id: user?.id || "demo-andini",
        volunteer_name: user?.name || "",
      });
      if (data.already_recorded) {
        toast.info("Kehadiran untuk aksi ini sudah tercatat sebelumnya.");
      } else {
        setSuccess(data);
        toast.success(`Kehadiran terverifikasi! +${data.hours} jam masuk ke profilmu.`);
        onVerified?.(data);
      }
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verifikasi gagal. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section aria-label="Verifikasi kehadiran relawan" data-testid="checkin-card"
      className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-7 mt-8">
      <h2 className="font-heading font-extrabold text-xl text-slate-900 flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-emerald-600" /> Verifikasi Kehadiran Aksi
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Masukkan kode PIN harian dari panitia di lokasi — tanpa kamera, tanpa sinyal kuat.
      </p>

      {success ? (
        <div data-testid="checkin-success" role="status"
          className="mt-5 bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-6 text-center">
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
          <p className="font-heading font-extrabold text-lg text-emerald-800 mt-2">Kehadiran Tercatat!</p>
          <p className="text-sm text-emerald-700 mt-1">
            <b>{success.event_title}</b> · +{success.hours} jam terverifikasi. Sertifikat digitalmu sudah aktif di Riwayat Aksi.
          </p>
          <Button data-testid="checkin-again-btn" onClick={() => setSuccess(null)} variant="outline"
            className="rounded-full font-bold mt-4">Catat Kehadiran Lain</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6 mt-5">
          <div className="space-y-3">
            <label htmlFor="checkin-event" className="text-sm font-bold text-slate-700 block">1. Pilih Kegiatan</label>
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger id="checkin-event" data-testid="checkin-event-select" className="rounded-xl h-12">
                <SelectValue placeholder="Pilih aksi yang kamu ikuti..." />
              </SelectTrigger>
              <SelectContent>
                {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <label htmlFor="checkin-pin" className="text-sm font-bold text-slate-700 block pt-1">2. Kode PIN Harian dari Panitia</label>
            <input
              id="checkin-pin"
              data-testid="checkin-pin-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="BN-429"
              maxLength={8}
              aria-label="Kode PIN harian kehadiran"
              className="w-full rounded-2xl h-16 text-center text-3xl font-black tracking-[0.25em] uppercase bg-slate-50 border-2 border-slate-300 text-slate-900 placeholder:text-slate-300 focus:border-emerald-600 focus:outline-none focus:ring-4 focus:ring-emerald-100 transition-colors"
            />
            <Button data-testid="checkin-verify-btn" onClick={() => verify("pin")} disabled={loading}
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 text-base">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verifikasi Kehadiran"}
            </Button>
          </div>
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-6 flex flex-col items-center justify-center text-center">
            <QrCode className="w-12 h-12 text-slate-400" aria-hidden="true" />
            <p className="text-sm font-bold text-slate-700 mt-3">Jalur Alternatif: Scan QR di Lokasi</p>
            <p className="text-xs text-slate-500 mt-1">Opsi pelengkap cepat bila poster QR tersedia di posko.</p>
            <Button data-testid="checkin-qr-btn" onClick={() => verify("qr")} disabled={loading} variant="outline"
              className="rounded-full font-bold mt-4">
              Simulasi Scan QR / Check-in
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
